/////////////////////////////////////////////////////////////////////////////////////////////////////////
// 화일명 : PwdResetModal.tsx
// 설명   : 비밀번호 찾기(재설정) 팝업 - 로그인 화면에서만 씀.
//         단계: 아이디 입력 -> 본인확인 방법 선택(OTP 앱 / 등록 이메일) -> 인증코드 입력 -> 새 비밀번호 설정.
//         - 기존 비밀번호를 알려주지 않고 새로 만든다(해시 저장 구조라 원래 값을 복원할 수 없음).
//         - 아이디 존재 여부는 드러내지 않음(서버가 같은 응답을 줌). 본인확인 실패는 재설정 전용 카운터로 셈.
//         - 스타일은 ChangePasswordModal과 같은 클래스(pwd-change-*)를 씀.
/////////////////////////////////////////////////////////////////////////////////////////////////////////
import React, { useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import '../styles/loginPage.css';
import { useCommonTranslation } from '../translations';
import { getMsgText } from '../utils/commonUtils';

type Step = 'id' | 'method' | 'code' | 'newpwd' | 'done';
type Method = 'TOTP' | 'EMAIL';

interface PwdResetModalProps {
  open: boolean;
  onClose: () => void;
}

const PwdResetModal: React.FC<PwdResetModalProps> = ({ open, onClose }) => {
  const { ddKey, language } = useCommonTranslation();
  const [step, setStep] = useState<Step>('id');
  const [userId, setUserId] = useState('');
  const [ticket, setTicket] = useState('');        // 1단계 티켓(아이디 확인)
  const [okTicket, setOkTicket] = useState('');    // 2단계 티켓(본인확인 통과)
  const [method, setMethod] = useState<Method | ''>('');
  const [code, setCode] = useState('');
  const [newPwd1, setNewPwd1] = useState('');
  const [newPwd2, setNewPwd2] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  if (!open) return null;

  const reset = () => {
    setStep('id'); setUserId(''); setTicket(''); setOkTicket(''); setMethod('');
    setCode(''); setNewPwd1(''); setNewPwd2(''); setBusy(false); setError(''); setInfo('');
  };
  const close = () => { reset(); onClose(); };

  // 발송 안내 + 1시간 안에 남은 발송 횟수(서버 remaining)
  const sentInfo = (rs: any) => `${rs.message || ''}${typeof rs.remaining === 'number' ? ` (1시간 내 남은 발송 ${rs.remaining}회)` : ''}`;
  const post = async (path: string, data: Record<string, any>) => {
    const res = await axios.post(`${API_BASE_URL}/com/${path}`, { langCd: language, ...data });
    return res.data;
  };

  // 1단계: 아이디 -> 재설정 티켓 + 방법 목록
  const handleStart = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      const rs = await post('apiPwdResetStart', { userId: userId.trim() });
      if (!rs.success) { setError(rs.message || '요청을 처리하지 못했습니다.'); return; }
      setTicket(rs.ticket);
      setStep('method');
    } catch (err) {
      setError(getMsgText(err, '요청을 처리하지 못했습니다.'));
    } finally { setBusy(false); }
  };

  // 2단계: 방법 선택 (이메일이면 코드를 먼저 보냄)
  const handlePickMethod = async (m: Method) => {
    setBusy(true); setError(''); setInfo(''); setMethod(m);
    try {
      if (m === 'EMAIL') {
        const rs = await post('apiPwdResetSendEmail', { ticket });
        if (!rs.success) { setError(rs.message || '인증코드를 보내지 못했습니다.'); return; }
        setInfo(sentInfo(rs));
      }
      setStep('code');
    } catch (err) {
      setError(getMsgText(err, '인증코드를 보내지 못했습니다.'));
    } finally { setBusy(false); }
  };

  const handleResend = async () => {
    setBusy(true); setError(''); setInfo('');
    try {
      const rs = await post('apiPwdResetSendEmail', { ticket });
      if (!rs.success) setError(rs.message || '인증코드를 보내지 못했습니다.');
      else setInfo(sentInfo(rs));
    } catch (err) {
      setError(getMsgText(err, '인증코드를 보내지 못했습니다.'));
    } finally { setBusy(false); }
  };

  // 3단계: 인증코드(OTP 또는 이메일) 확인 -> 2단계 티켓
  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      const rs = await post('apiPwdResetVerify', { ticket, method, code: code.trim() });
      if (!rs.success) { setError(rs.message || '인증코드가 올바르지 않습니다.'); return; }
      setOkTicket(rs.ticket);
      setStep('newpwd');
    } catch (err) {
      setError(getMsgText(err, '인증코드 확인 중 오류가 발생했습니다.'));
    } finally { setBusy(false); }
  };

  // 4단계: 새 비밀번호 저장
  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPwd1 !== newPwd2) { setError('새 비밀번호가 서로 일치하지 않습니다.'); return; }
    setBusy(true); setError('');
    try {
      const rs = await post('apiPwdResetConfirm', { ticket: okTicket, newPassword: newPwd1 });
      if (!rs.success) { setError(rs.message || '비밀번호를 재설정하지 못했습니다.'); return; }
      setInfo(rs.message || '');
      setStep('done');
    } catch (err) {
      setError(getMsgText(err, '비밀번호 재설정 중 오류가 발생했습니다.'));
    } finally { setBusy(false); }
  };

  const HINT: React.CSSProperties = { color: '#64748b', fontSize: 12, margin: '0 0 14px' };
  const errorLine = error ? <p className="pwd-change-error">{error}</p> : null;

  return (
    <div className="pwd-change-overlay">
      <div className="pwd-change-card">
        <h3 className="pwd-reset-title" style={{ fontWeight: 700, color: '#2d3748', margin: '0 0 10px' }}>{ddKey('lblPwdResetTitle') || '비밀번호 찾기'}</h3>

        {step === 'id' && (
          <form className="login-form" onSubmit={handleStart}>
            <p className="pwd-change-desc">{ddKey('msgPwdResetIdDesc') || '아이디를 입력하세요.'}</p>
            <div className="form-group">
              <label htmlFor="pwdResetId" className="form-label">{ddKey('userId') || '사용자 ID'}</label>
              <input id="pwdResetId" type="text" required className="form-input" autoFocus
                value={userId} onChange={(e) => setUserId(e.target.value)} disabled={busy}
                autoComplete="username" autoCapitalize="none" autoCorrect="off" spellCheck={false} />
            </div>
            {errorLine}
            <div className="form-group pwd-change-actions">
              <button type="submit" className={`login-btn ${busy ? 'loading' : ''}`} disabled={busy}>
                {ddKey('btnNext') || '다음'}
              </button>
              <button type="button" className="pwd-change-cancel-btn" onClick={close} disabled={busy}>
                {ddKey('btnCancel') || '취소'}
              </button>
            </div>
          </form>
        )}

        {step === 'method' && (
          <div className="login-form">
            <p className="pwd-change-desc">{ddKey('msgPwdResetMethodDesc') || '본인확인 방법을 선택하세요.'}</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14 }}>
              <button type="button" className={`login-btn ${busy ? 'loading' : ''}`} disabled={busy}
                onClick={() => handlePickMethod('TOTP')}>
                {ddKey('lblPwdResetByOtp') || 'OTP 앱 코드로 확인'}
              </button>
              <button type="button" className="login-btn" disabled={busy}
                onClick={() => handlePickMethod('EMAIL')}>
                {ddKey('lblPwdResetByEmail') || '등록 이메일로 확인'}
              </button>
            </div>
            <p style={HINT}>
              {ddKey('msgPwdResetMethodHint') || 'OTP 앱을 등록하지 않았거나 이메일이 없으면 관리자에게 초기화를 요청하세요.'}
            </p>
            {errorLine}
            <div className="form-group pwd-change-actions">
              <button type="button" className="pwd-change-cancel-btn" onClick={close} disabled={busy}>
                {ddKey('btnCancel') || '취소'}
              </button>
            </div>
          </div>
        )}

        {step === 'code' && (
          <form className="login-form" onSubmit={handleVerify}>
            <p className="pwd-change-desc">
              {method === 'TOTP'
                ? (ddKey('msgPwdResetOtpDesc') || 'OTP 앱에 표시된 6자리 코드를 입력하세요.')
                : (ddKey('msgPwdResetEmailDesc') || '등록 이메일로 받은 6자리 코드를 입력하세요.')}
            </p>
            {info && <p style={HINT}>{info}</p>}
            <div className="form-group">
              <label htmlFor="pwdResetCode" className="form-label">{ddKey('lblAuthCode') || '인증코드'}</label>
              <input id="pwdResetCode" type="text" inputMode="numeric" required maxLength={6} className="form-input" autoFocus
                value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} disabled={busy} />
            </div>
            {errorLine}
            <div className="form-group pwd-change-actions">
              <button type="submit" className={`login-btn ${busy ? 'loading' : ''}`} disabled={busy || code.length !== 6}>
                {ddKey('btnConfirm') || '확인'}
              </button>
              {method === 'EMAIL' && (
                <button type="button" className="pwd-change-cancel-btn" onClick={handleResend} disabled={busy}>
                  {ddKey('btnMfaResend') || '코드 재발송'}
                </button>
              )}
              <button type="button" className="pwd-change-cancel-btn" onClick={close} disabled={busy}>
                {ddKey('btnCancel') || '취소'}
              </button>
            </div>
          </form>
        )}

        {step === 'newpwd' && (
          <form className="login-form" onSubmit={handleConfirm}>
            <p className="pwd-change-desc">{ddKey('msgPwdChangeDesc') || '새 비밀번호를 입력하세요.'}</p>
            <div className="form-group">
              <label htmlFor="pwdResetNew1" className="form-label">{ddKey('newPassword') || '새 비밀번호'}</label>
              <input id="pwdResetNew1" type="password" required className="form-input" autoFocus
                value={newPwd1} onChange={(e) => setNewPwd1(e.target.value)} disabled={busy} />
            </div>
            <div className="form-group">
              <label htmlFor="pwdResetNew2" className="form-label">{ddKey('newPasswordConfirm') || '새 비밀번호 확인'}</label>
              <input id="pwdResetNew2" type="password" required className="form-input"
                value={newPwd2} onChange={(e) => setNewPwd2(e.target.value)} disabled={busy} />
            </div>
            <p style={HINT}>
              {ddKey('msgPwdPolicyHint') || '최소 10자 이상, 영문 대/소문자·숫자·특수문자 중 3종류 이상 조합, 아이디 포함 불가'}
            </p>
            {errorLine}
            <div className="form-group pwd-change-actions">
              <button type="submit" className={`login-btn ${busy ? 'loading' : ''}`} disabled={busy}>
                {ddKey('btnChangePassword') || '비밀번호 변경'}
              </button>
              <button type="button" className="pwd-change-cancel-btn" onClick={close} disabled={busy}>
                {ddKey('btnCancel') || '취소'}
              </button>
            </div>
          </form>
        )}

        {step === 'done' && (
          <div className="login-form">
            <p className="pwd-change-desc">{info}</p>
            <div className="form-group pwd-change-actions">
              <button type="button" className="login-btn" onClick={close}>
                {ddKey('btnConfirm') || '확인'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PwdResetModal;
